/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, February 23rd 2022, 11:14:47 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { FormConfiguration } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import Stack from "@mui/material/Stack";
import { OSActionButton, OSActionButtonProps, ActionDialog, useToggleState } from "ui-widgets";
import { AddNewFormButton, AddNewFormButtonProps } from "form-builder/components/AddNewFormButton";
import { AddExistingFormButton } from "form-builder/components/AddExistingFormButton";
import { useListEditorContext } from "lib/providers";
import { APIListProgressWrapper, APIListProgressWrapperProps } from "ui-widgets/wrappers/APIRequestWrappers";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid";
import { FileText } from "react-feather";
import { COLORSCHEME } from "theme/styles";
import { useFormBuilderContext } from "form-builder/providers";
import { EmptyFormsCTA } from "./EmptyFormsCTA";

interface FormSimpleListEditorProps
  extends Pick<
      AddNewFormButtonProps,
      "popUpTitle" | "popUpDescription" | "allowedFormTypes" | "defaultNewFormConfiguration"
    >,
    Pick<FormConfiguration, "form_type">,
    Pick<APIListProgressWrapperProps<FormConfiguration>, "noDataMessage"> {
  onRemoveForm?: (form: FormConfiguration) => void;
  onSaveFormInfo?: (form: FormConfiguration) => void;
}

export const FormSimpleListEditor = (props: FormSimpleListEditorProps) => {
  const {
    itemsListState: { savedItems: formsList, selectedItemId: selectedFormId },
    isFetchingItems: isFetchingFormsList,
    fetchItemsStatusCode: fetchFormsListStatusCode,
    fetchItemsHasError: fetchFormsListHasError,
    updateList,
    removeItem: removeForm,
    selectItem: selectForm,
  } = useListEditorContext<FormConfiguration>();

  const { onRemoveForm, onSaveFormInfo } = props;

  const handleRemoveForm = React.useCallback(
    (form: FormConfiguration) => {
      onRemoveForm?.(form);
      removeForm(form.id);
    },
    [onRemoveForm, removeForm]
  );

  const handleSuccessfulFormInfoSaveCallback = React.useCallback(
    (updatedFormConfig: FormConfiguration) => {
      updateList(updatedFormConfig);
      onSaveFormInfo?.(updatedFormConfig);
    },
    [updateList, onSaveFormInfo] //Ensuring we always call the correct instance of the memoized closeFormInfoPopUpFn and onAPISaveFormInfoCallBackFn functions
  );

  const { useNavigateToFormBuilder } = useFormBuilderContext();
  const { navigateToFormBuilder } = useNavigateToFormBuilder();

  React.useEffect(() => {
    selectedFormId && navigateToFormBuilder(selectedFormId);
  }, [selectedFormId, navigateToFormBuilder]);

  const getAddNewFormButtons = (size?: OSActionButtonProps["size"]) => (
    <>
      <AddNewFormButton
        onAPISaveFormInfoCallBackFn={handleSuccessfulFormInfoSaveCallback}
        popUpTitle={props.popUpTitle}
        popUpDescription={props.popUpDescription}
        form_type={props.form_type}
        size={size}
        allowedFormTypes={props.allowedFormTypes}
        defaultNewFormConfiguration={props.defaultNewFormConfiguration}
      />
      <AddExistingFormButton
        form_type={props.form_type}
        onChooseForm={handleSuccessfulFormInfoSaveCallback}
        size={size}
        allowedFormTypes={props.allowedFormTypes}
      />
    </>
  );

  return (
    <APIListProgressWrapper
      items={formsList}
      showLoader={isFetchingFormsList}
      httpResponseStatusCode={fetchFormsListStatusCode}
      isAPIError={fetchFormsListHasError}
      noDataMessage={<EmptyFormsCTA noDataMessage={props.noDataMessage} ctaButtons={getAddNewFormButtons("large")} />}
    >
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={2}>
          {getAddNewFormButtons()}
        </Stack>
        <FormsList
          formsList={formsList}
          onSelectForm={(id: PrimaryKeyValue<FormConfiguration>) => selectForm(id)}
          onRemoveForm={handleRemoveForm}
        />
      </Stack>
    </APIListProgressWrapper>
  );
};

interface FormsListProps {
  formsList: FormConfiguration[];
  onSelectForm: (id: FormConfiguration["id"]) => void;
  onRemoveForm: (form: FormConfiguration) => void;
}

const FormsList = ({ formsList, onSelectForm, onRemoveForm }: FormsListProps) => {
  return (
    <Stack direction="column" spacing={2}>
      <List component="nav" aria-label="forms-list">
        {formsList.map((form) => (
          <FormListItem
            form={form}
            handleSelectForm={(form) => onSelectForm(form.id)}
            handleRemoveForm={(form) => onRemoveForm(form)}
          />
        ))}
      </List>
    </Stack>
  );
};

interface FormListItemProps {
  form: FormConfiguration;
  handleSelectForm: (form: FormConfiguration) => void;
  handleRemoveForm: (form: FormConfiguration) => void;
}

const FormListItem = ({ handleRemoveForm, form, handleSelectForm }: FormListItemProps) => {
  const {
    handleClose: closeDeleteConfirmationFn,
    handleOpen: openDeleteConfirmationFn,
    open: deleteConfirmationOpened,
  } = useToggleState();

  return (
    <>
      <ListItemButton color={COLORSCHEME.secondaryVariant} key={`${form.id}`}>
        <Grid container>
          <Grid item md={1}>
            <FileText color={COLORSCHEME.primary} />
          </Grid>
          <Grid item md={7}>
            <ListItemText primary={form.title} />
          </Grid>
          <Grid item md={4}>
            <Stack direction="row" spacing={10}>
              <OSActionButton actionStyle="edit" variant="text" onClick={() => handleSelectForm(form)}>
                Edit Form
              </OSActionButton>
              <OSActionButton actionStyle="delete" variant="text" onClick={openDeleteConfirmationFn}>
                Remove Form
              </OSActionButton>
            </Stack>
          </Grid>
        </Grid>
      </ListItemButton>
      <ActionDialog
        open={deleteConfirmationOpened}
        onClose={closeDeleteConfirmationFn}
        title="Remove Form from this Service"
        dialogMessage="Are you sure you want to remove this form from this service?"
        severity="warning"
        confirmationAction={() => {
          handleRemoveForm(form);
          closeDeleteConfirmationFn();
        }}
      />
    </>
  );
};
